import React, { useMemo, useState } from 'react';
import styles from './Dashboard.module.css';
import DateRangePicker from '../components/DateRangePicker';
import { useCountry } from '../context/CountryContext';
import {
  DEFAULT_SELECTION, resolveRange, previousPeriod, comparisonLabel, formatRange, eachDay,
  toISODate, formatShortDay, formatShortMonth, daysInRange,
} from '../utils/dateRange';

const KPI_DEFS = [
  { key: 'orders', label: 'Orders', color: 'blue', format: 'number', base: 142 },
  { key: 'cancellations', label: 'Cancellations', color: 'red', format: 'number', base: 12 },
  { key: 'profit', label: 'Profit', color: 'green', format: 'currency', base: 3200 },
  { key: 'losses', label: 'Losses', color: 'red', format: 'currency', base: 480 },
  { key: 'accidents', label: 'Accidents', color: 'red', format: 'number', base: 3 },
  { key: 'activeVehicles', label: 'Active vehicles', color: 'blue', format: 'number', base: 86, agg: 'avg' },
  { key: 'partnerRevenue', label: 'Partner revenue', color: 'green', format: 'currency', base: 12500 },
  { key: 'newUsers', label: 'New users', color: 'blue', format: 'number', base: 38 },
  { key: 'completedRides', label: 'Completed rides', color: 'green', format: 'number', base: 118 },
  { key: 'pendingBookings', label: 'Pending bookings', color: 'yellow', format: 'number', base: 24 },
  { key: 'conversionRate', label: 'Conversion rate', color: 'green', format: 'percent', base: 68 },
  { key: 'avgRideValue', label: 'Average ride value', color: 'blue', format: 'currency', base: 24, agg: 'avg' },
  { key: 'cancellationRate', label: 'Cancellation rate', color: 'yellow', format: 'percent', base: 8 },
  { key: 'accidentRate', label: 'Accident rate', color: 'red', format: 'percent', base: 2 },
  { key: 'platformGrowth', label: 'Platform growth', color: 'green', format: 'percent', base: 14 },
];

const PARTNERS_BASE = [
  { company: 'City Cabs', rides: 1240, revenue: 45800, profit: 8200, cancellations: 4.2, accidents: 2, net: 92 },
  { company: 'Green Wheels', rides: 980, revenue: 38200, profit: 6100, cancellations: 2.8, accidents: 0, net: 96 },
  { company: 'Quick Scoot', rides: 760, revenue: 19400, profit: 3100, cancellations: 6.5, accidents: 1, net: 84 },
  { company: 'Metro Transit', rides: 2100, revenue: 72000, profit: 5400, cancellations: 1.9, accidents: 0, net: 88 },
  { company: 'Swift Ride', rides: 540, revenue: 22100, profit: 1200, cancellations: 9.1, accidents: 3, net: 64 },
  { company: 'Alpha Drivers', rides: 1500, revenue: 61000, profit: 10500, cancellations: 3.4, accidents: 1, net: 93 },
  { company: 'Urban Rentals', rides: 430, revenue: 15600, profit: 2800, cancellations: 5.7, accidents: 2, net: 78 },
  { company: 'Zoom Scooters', rides: 890, revenue: 26300, profit: 4500, cancellations: 4.0, accidents: 0, net: 91 },
];

const COLUMNS = [
  { key: 'company', label: 'Company name', align: 'left', format: 'text' },
  { key: 'rides', label: 'Number of rides', align: 'right', format: 'number' },
  { key: 'revenue', label: 'Revenue', align: 'right', format: 'currency' },
  { key: 'profit', label: 'Profit', align: 'right', format: 'currency' },
  { key: 'cancellations', label: 'Cancellation rate', align: 'right', format: 'percent' },
  { key: 'accidents', label: 'Accident reports', align: 'right', format: 'number' },
  { key: 'net', label: 'Net performance', align: 'right', format: 'number' },
];

function formatNumber(n) {
  return n.toLocaleString();
}

function formatCurrency(n) {
  return `$${n.toLocaleString()}`;
}

function formatPercent(n) {
  return `${n}%`;
}

function formatValue(value, format) {
  if (format === 'currency') return formatCurrency(value);
  if (format === 'percent') return formatPercent(value);
  return formatNumber(value);
}

// ── Sample data, driven by ONE selection ──────────────────────────────
// The dashboard has no real statistics source yet: these figures are
// sample values (as before), but they are now produced per calendar day
// from the single { range, country } selection, so every KPI, the trend
// chart, the partner comparison and the comparison percentages always
// describe exactly the same period and country. Swapping in a real API
// later only means replacing dailyValue() / buildDashboardData().
function hash01(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0) / 4294967295;
}

function countryShare(country) {
  if (country === 'NONE') return 0;
  if (country === 'ALL') return 1;
  return 0.35 + hash01(`country:${country}`) * 0.4;
}

function dailyValue(def, day, country) {
  const noise = 0.75 + hash01(`${def.key}|${toISODate(day)}|${country}`) * 0.5;
  if (def.format === 'percent') return def.base + (noise - 1) * 4;
  return def.base * noise * countryShare(country);
}

function aggregate(def, days, country) {
  if (country === 'NONE' || !days.length) return 0;
  const values = days.map((d) => dailyValue(def, d, country));
  const sum = values.reduce((a, b) => a + b, 0);
  if (def.format === 'percent') return Math.round(Math.min(100, Math.max(0, sum / values.length)) * 10) / 10;
  // Averages and stock figures (active vehicles) do not grow with the
  // length of the period -- they are the daily average, not a total.
  if (def.agg === 'avg') return Math.round(sum / values.length);
  return Math.round(sum);
}

const ORDERS_DEF = KPI_DEFS.find((d) => d.key === 'orders');
const CANCEL_DEF = KPI_DEFS.find((d) => d.key === 'cancellations');

// Trend buckets: hourly for a single day, daily up to ~2 months, monthly beyond.
function buildTrend(range, country) {
  const days = eachDay(range);
  const buckets = [];
  if (days.length === 1) {
    const weights = Array.from({ length: 24 }, (_, h) => 0.2 + Math.sin(((h - 6) / 24) * Math.PI) ** 2 + hash01(`hour|${h}|${toISODate(days[0])}`) * 0.3);
    const total = weights.reduce((a, b) => a + b, 0);
    weights.forEach((w, h) => buckets.push({ label: `${String(h).padStart(2, '0')}:00`, days, weight: w / total }));
  } else if (days.length <= 62) {
    days.forEach((d) => buckets.push({ label: formatShortDay(d), days: [d], weight: 1 }));
  } else {
    days.forEach((d) => {
      const label = formatShortMonth(d);
      const last = buckets[buckets.length - 1];
      if (last && last.label === label) last.days.push(d);
      else buckets.push({ label, days: [d], weight: 1 });
    });
  }
  const labels = [];
  const orders = [];
  const cancellations = [];
  const wins = [];
  buckets.forEach((b) => {
    const o = Math.round(aggregate(ORDERS_DEF, b.days, country) * b.weight);
    const c = Math.round(aggregate(CANCEL_DEF, b.days, country) * b.weight);
    labels.push(b.label);
    orders.push(o);
    cancellations.push(c);
    wins.push(Math.max(0, o - c));
  });
  return { labels, orders, cancellations, wins };
}

function buildDashboardData(range, country) {
  const days = eachDay(range);
  const prevDays = eachDay(previousPeriod(range));
  const kpis = KPI_DEFS.map((def) => {
    const value = aggregate(def, days, country);
    const prev = aggregate(def, prevDays, country);
    const change = prev ? Math.round(((value - prev) / prev) * 100) : 0;
    return { ...def, value, change };
  });
  const scale = (days.length / 30) * countryShare(country);
  const partners = PARTNERS_BASE.map((p) => {
    const f = scale * (0.85 + hash01(`${p.company}|${toISODate(range.from)}|${toISODate(range.to)}|${country}`) * 0.3);
    return {
      ...p,
      rides: Math.round(p.rides * f),
      revenue: Math.round(p.revenue * f),
      profit: Math.round(p.profit * f),
      accidents: Math.round(p.accidents * scale),
    };
  });
  return { kpis, trend: buildTrend(range, country), partners };
}

function buildPoints(values, max) {
  const n = values.length;
  if (n === 0) return '';
  if (n === 1) return '50,20';
  const step = 100 / (n - 1);
  return values
    .map((v, i) => {
      const x = step * i;
      const y = 38 - (v / (max || 1)) * 36;
      return `${x},${y}`;
    })
    .join(' ');
}

export default function Dashboard() {
  const { country, availableOptions } = useCountry();
  const [selection, setSelection] = useState(DEFAULT_SELECTION);
  const [sort, setSort] = useState({ key: 'net', asc: false });

  // "Today" is re-read on every render, so a page left open past midnight
  // resolves "1 day" to the new calendar day.
  const todayIso = toISODate(new Date());
  const today = useMemo(() => new Date(`${todayIso}T00:00:00`), [todayIso]);
  const range = useMemo(() => resolveRange(selection, today) || resolveRange(DEFAULT_SELECTION, today), [selection, today]);
  const prevRange = useMemo(() => previousPeriod(range), [range]);
  const compareText = comparisonLabel(selection, range);
  const countryName = country === 'ALL'
    ? 'All Countries'
    : country === 'NONE'
      ? 'No active country'
      : (availableOptions.find((o) => o.code === country)?.label || country);

  // One selection -> every section of the dashboard.
  const { kpis, trend, partners } = useMemo(() => buildDashboardData(range, country), [range, country]);

  const sortedPartners = useMemo(() => {
    const list = [...partners];
    list.sort((a, b) => {
      const va = a[sort.key];
      const vb = b[sort.key];
      if (typeof va === 'string') return sort.asc ? va.localeCompare(vb) : vb.localeCompare(va);
      return sort.asc ? va - vb : vb - va;
    });
    return list;
  }, [partners, sort]);

  const maxTrend = useMemo(
    () => Math.max(...trend.orders, ...trend.cancellations, ...trend.wins, 1),
    [trend]
  );

  const ordersPoints = useMemo(() => buildPoints(trend.orders, maxTrend), [trend, maxTrend]);
  const cancellationPoints = useMemo(() => buildPoints(trend.cancellations, maxTrend), [trend, maxTrend]);
  const winsPoints = useMemo(() => buildPoints(trend.wins, maxTrend), [trend, maxTrend]);

  const handleSort = (key) => {
    setSort((s) => ({ key, asc: s.key === key ? !s.asc : true }));
  };

  const visibleLabels = useMemo(() => {
    const { labels } = trend;
    const step = Math.ceil(labels.length / 6);
    return labels.map((l, i) => (i % step === 0 || i === labels.length - 1 ? l : ''));
  }, [trend]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>Real-time overview of platform performance</p>
        </div>
        <DateRangePicker value={selection} onChange={setSelection} today={today} />
      </header>

      <dl className={styles.selectionSummary} aria-live="polite">
        <div className={styles.summaryItem}>
          <dt>Date range</dt>
          <dd>
            {formatRange(range)}
            <span className={styles.summaryMuted}> · {daysInRange(range) === 1 ? '1 day' : `${daysInRange(range)} days`}</span>
          </dd>
        </div>
        <div className={styles.summaryItem}>
          <dt>Country</dt>
          <dd>{countryName}</dd>
        </div>
        <div className={styles.summaryItem}>
          <dt>Comparison</dt>
          <dd>
            Previous period of the same length
            <span className={styles.summaryMuted}> · {formatRange(prevRange)}</span>
          </dd>
        </div>
      </dl>

      <section className={styles.kpiGrid}>
        {kpis.map((kpi) => (
          <div key={kpi.key} className={`${styles.kpiCard} ${styles[kpi.color]}`}>
            <div className={styles.kpiTop}>
              <span className={styles.kpiLabel}>{kpi.label}</span>
              <span className={styles.kpiDot} />
            </div>
            <div className={styles.kpiValue}>{formatValue(kpi.value, kpi.format)}</div>
            <div className={`${styles.kpiChange} ${kpi.change >= 0 ? styles.kpiUp : styles.kpiDown}`}>
              {kpi.change >= 0 ? '▲' : '▼'} {Math.abs(kpi.change)}% {compareText}
            </div>
          </div>
        ))}
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Trend: Orders, Cancellations & Wins</h2>
          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.blueDot}`} />
              Orders
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.redDot}`} />
              Cancellations
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.greenDot}`} />
              Wins
            </span>
          </div>
        </div>
        <div className={styles.chartWrap}>
          <svg viewBox="0 0 100 40" className={styles.chartSvg} preserveAspectRatio="none">
            <line x1="0" y1="0" x2="0" y2="40" className={styles.gridLine} />
            <line x1="25" y1="0" x2="25" y2="40" className={styles.gridLine} />
            <line x1="50" y1="0" x2="50" y2="40" className={styles.gridLine} />
            <line x1="75" y1="0" x2="75" y2="40" className={styles.gridLine} />
            <line x1="100" y1="0" x2="100" y2="40" className={styles.gridLine} />
            <line x1="0" y1="10" x2="100" y2="10" className={styles.gridLine} />
            <line x1="0" y1="20" x2="100" y2="20" className={styles.gridLine} />
            <line x1="0" y1="30" x2="100" y2="30" className={styles.gridLine} />
            <polyline points={ordersPoints} className={styles.lineOrders} />
            <polyline points={cancellationPoints} className={styles.lineCancellations} />
            <polyline points={winsPoints} className={styles.lineWins} />
          </svg>
          <div className={styles.xLabels}>
            {visibleLabels.map((l, i) => (
              <span key={i} className={styles.xLabel}>
                {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Partner Comparison</h2>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className={`${styles.th} ${col.align === 'right' ? styles.thRight : styles.thLeft}`}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {sort.key === col.key && (
                      <span className={styles.sortIcon}>{sort.asc ? ' ▲' : ' ▼'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPartners.map((p) => (
                <tr key={p.company} className={styles.tr}>
                  {COLUMNS.map((col) => {
                    let val = p[col.key];
                    if (col.format === 'currency') val = formatCurrency(val);
                    else if (col.format === 'percent') val = `${val}%`;
                    else if (col.format === 'number') val = formatNumber(val);
                    return (
                      <td
                        key={col.key}
                        className={`${styles.td} ${col.align === 'right' ? styles.tdRight : styles.tdLeft}`}
                      >
                        {col.key === 'company' ? (
                          <span className={styles.companyName}>{val}</span>
                        ) : col.key === 'net' ? (
                          <span
                            className={`${styles.netScore} ${
                              val >= 85 ? styles.netHigh : val >= 70 ? styles.netMid : styles.netLow
                            }`}
                          >
                            {val}
                          </span>
                        ) : (
                          val
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
